import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";

interface MobileTableCardProps {
  children: ReactNode;
  className?: string;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function MobileTableCard({
  children,
  className,
  isSelected,
  onSelect,
}: MobileTableCardProps) {
  return (
    <Card
      className={cn(
        "transition-all duration-200",
        isSelected && "ring-2 ring-primary bg-muted/50",
        className
      )}
    >
      <CardContent className="p-4 space-y-3">
        {onSelect && (
          <div className="flex items-center justify-between mb-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onSelect}
              className="h-5 w-5 rounded border-input text-primary focus:ring-primary cursor-pointer"
            />
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

interface MobileTableCardRowProps {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  className?: string;
}

export function MobileTableCardRow({
  label,
  value,
  icon: Icon,
  className,
}: MobileTableCardRowProps) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 min-h-[44px]",
        className
      )}
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="font-medium">{label}</span>
      </div>
      <div className="text-sm font-medium text-right min-w-0 flex-1 truncate">
        {value}
      </div>
    </div>
  );
}

interface MobileTableCardActionsProps {
  children: ReactNode;
  className?: string;
}

export function MobileTableCardActions({
  children,
  className,
}: MobileTableCardActionsProps) {
  return (
    <div
      className={cn("flex items-center gap-2 pt-3 border-t mt-3", className)}
    >
      {children}
    </div>
  );
}

interface MobileTableCardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
}

export function MobileTableCardHeader({
  title,
  subtitle,
  badge,
  actions,
}: MobileTableCardHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-3 pb-3 border-b">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-semibold text-base truncate">{title}</h4>
          {badge}
        </div>
        {subtitle && (
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
        )}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}
